import { memo, useState } from "react";
import { classNames, useTranslate } from "helpers";
import { ReactSVG } from "react-svg";
import { ContractSellerCardProps } from "./types";
import { SELLER_FIELDS } from "./consts";
import ContractSellerEditCard from "../ContractSellerEditCard/ContractSellerEditCard";

const InfoRow: React.FC<{ label: string; value: string | null; hasError?: boolean }> = ({ label, value, hasError }) => (
  <div className="flex justify-between py-1.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={classNames(
      "text-sm font-medium",
      value ? "text-green-700" : hasError ? "text-red-500" : "text-slate-900"
    )}>{value || (hasError ? "Potrebno popuniti" : "—")}</span>
  </div>
);

const ContractSellerCard: React.FC<ContractSellerCardProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
}) => {
  const { t } = useTranslate();
  const [showEditModal, setShowEditModal] = useState(false);

  const hasErrors = SELLER_FIELDS.some(field => errors[field]);

  const handleOpenModal = () => {
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  const handleSave = (values: Partial<typeof formValues>) => {
    setFormValues(prev => ({ ...prev, ...values }));
    setErrors(prev => {
      const next = { ...prev };
      SELLER_FIELDS.forEach(field => delete next[field]);
      return next;
    });
  };

  return (
    <>
      <div className={classNames(
        "flex-1 bg-white rounded-sm border overflow",
        hasErrors ? "border-red-300" : "border-slate-300"
      )}>
        <div className={classNames(
          "flex justify-between items-center px-3.5 py-2.5",
          hasErrors ? "bg-red-50" : "bg-slate-50"
        )}>
          <div className="flex items-center gap-2">
            <span className="text-md font-medium text-slate-900">Najmodavac</span>
            {hasErrors && (
              <span className="w-2 h-2 rounded-full bg-red-500" />
            )}
          </div>
          <button type="button" onClick={handleOpenModal} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-sky-500 bg-sky-50 text-sky-700 text-sm font-medium hover:bg-sky-100 transition-colors">
            <ReactSVG src="/media/icons/edit.svg" className="[&_svg]:w-3.5 [&_svg]:h-3.5 [&_path]:stroke-sky-600" />
            Uredi
          </button>
        </div>
       <div className={classNames("flex flex-col px-3.5 py-3", hasErrors && "bg-white")}>
          <InfoRow label={t("admin.fleet.vehicle.contract.add.sellercompanyname")} value={formValues.lessorCompanyName} hasError={!!errors.lessorCompanyName} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.sellerpersonalidentificationnumber")} value={formValues.lessorPersonalIdentificationNumber} hasError={!!errors.lessorPersonalIdentificationNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.sellerheadquartersaddress")} value={formValues.lessorHeadquartersAddress} hasError={!!errors.lessorHeadquartersAddress} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.sellertransactionaccountnumber")} value={formValues.lessorTransactionAccountNumber} hasError={!!errors.lessorTransactionAccountNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.sellersalespersonname")} value={formValues.lessorSalesPersonName} hasError={!!errors.lessorSalesPersonName} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.sellerphonenumber")} value={formValues.lessorPhoneNumber} hasError={!!errors.lessorPhoneNumber} />
        </div>
      </div>

      <ContractSellerEditCard
        show={showEditModal}
        formValues={formValues}
        errors={errors}
        onSave={handleSave}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default memo(ContractSellerCard);
